import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import {
  type Control,
  type FieldValues,
  type UseControllerProps,
  useController,
} from 'react-hook-form'
import { useResizeDetector } from 'react-resize-detector'
import SignaturePad, {
  type Options,
} from '../../lib/signature_pad/signature_pad'

const placeholderStyles: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  width: '100%',
  height: '100%',
  color: 'var(--ion-color-step-300, #b3b3b3)',
  fontSize: '2.5em',
  fontWeight: 300,
  letterSpacing: '0.05em',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  pointerEvents: 'none',
  userSelect: 'none',
  transition: 'opacity 0.3s ease',
}

export type SignatureProps = {
  name: string
  control: Control<FieldValues>
  rules?: UseControllerProps['rules']
  defaultValue?: string
  shouldUnregister?: boolean
  options?: Options
  style?: React.CSSProperties
}

export const Signature = (props: SignatureProps) => {
  const {
    name,
    rules,
    control,
    defaultValue,
    shouldUnregister,
    style,
    options,
  } = props

  const { _ } = useLingui()
  const canvas = useRef<HTMLCanvasElement>(null)
  const signatureRef = useRef<SignaturePad | null>(null)
  const [hasStarted, setHasStarted] = useState<boolean>(false)

  const {
    field: { onChange, value },
  } = useController({
    control,
    defaultValue: defaultValue !== undefined ? defaultValue : '',
    name,
    rules,
    shouldUnregister,
  })

  // Use refs for callbacks so the SignaturePad always calls the latest version
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange
  const optionsRef = useRef(options)
  optionsRef.current = options

  // Initialize SignaturePad once
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally run once on mount — pad initializes with canvas ref
  useEffect(() => {
    if (!canvas.current) return

    const pad = new SignaturePad(canvas.current, {
      ...options,
      onBegin: (evt: MouseEvent | Touch) => {
        setHasStarted(true)
        optionsRef.current?.onBegin?.(evt)
      },
      onEnd: (evt: MouseEvent | Touch) => {
        onChangeRef.current(pad.toDataURL('image/svg+xml'))
        optionsRef.current?.onEnd?.(evt)
      },
    })
    signatureRef.current = pad

    return () => {
      pad.off()
    }
  }, [])

  // Handle resize via callback instead of useEffect
  const { ref: containerRef } = useResizeDetector<HTMLDivElement>({
    onResize: ({
      width,
      height,
    }: { width?: number | null; height?: number | null }) => {
      if (!width || !height) return
      if (!canvas.current) return

      const ratio = Math.max(window.devicePixelRatio || 1, 1)
      canvas.current.width = width * ratio
      canvas.current.height = height * ratio
      canvas.current.getContext('2d')?.scale(ratio, ratio)
      canvas.current.style.width = `${width}px`
      canvas.current.style.height = `${height}px`
      signatureRef.current?.clear()

      if (value) {
        signatureRef.current?.fromDataURL(value, {
          ratio,
          width: canvas.current.width,
          height: canvas.current.height,
        })
      }
    },
  })

  // If value is set externally, mark as started
  useEffect(() => {
    if (value) setHasStarted(true)
  }, [value])

  return (
    <div
      ref={containerRef}
      style={{
        minHeight: '200px',
        position: 'relative',
        borderBottom: '1px solid rgba(0, 0, 0, 0.15)',
        overflow: 'hidden',
        ...style,
      }}
    >
      {/* Absolutely positioned so the canvas never contributes to the
          container's height. Otherwise onResize sets the canvas height from the
          container, which grows the container, which retriggers onResize — an
          infinite expansion loop. */}
      <canvas
        ref={canvas}
        style={{ position: 'absolute', top: 0, left: 0, zIndex: 4 }}
      />
      {!hasStarted && (
        <div style={placeholderStyles}>
          <span>{_(msg`Sign Here`)}</span>
        </div>
      )}
    </div>
  )
}
